/**
 * Wide writes replace a large slice of the version; persist the full gameConfig
 * blob instead of a dotted path $set (copy-all-levels, language remove, …).
 *
 * @param {string} path
 * @returns {boolean}
 */
export function isWidePersistPath(path) {
  return path === "gameConfig" || path === "gameConfig.components";
}

/** Roots version/updateOne accepts as dotted $set paths (see server persistFields). */
const PERSISTABLE_ROOTS = ["gameConfig", "createdLanguages", "recorderSettings"];

/**
 * The server rejects a `fields` body containing any other root with
 * INVALID_FIELDS, so writes like `lastUpdatingUserID` (version/folder rename)
 * have to take the full-blob save, which carries those columns of its own.
 *
 * @param {string} path
 * @returns {boolean}
 */
export function hasPersistableRoot(path) {
  return PERSISTABLE_ROOTS.some((root) => path === root || path.startsWith(`${root}.`));
}

/**
 * Version paths are lodash paths, but they are sent as Mongo $set keys and Mongo
 * only understands dots: an array index (`objectsInfos[0].position`) would land
 * as a literal `objectsInfos[0]` field beside the real array, where nothing
 * reads it again. Persist the array itself instead — the in-memory array already
 * carries the edit, and replacing it whole cannot pad the stored array with
 * nulls the way `objectsInfos.0` would when the document is shorter.
 *
 * @param {string} path
 * @returns {string} path to $set, "" when the path starts with an index
 */
export function toPersistPath(path) {
  const bracket = path.indexOf("[");
  if (bracket < 0) return path;
  return path.slice(0, bracket).replace(/\.$/, "");
}

/**
 * Accumulate the paths written across the persist debounce window. Values are
 * read off the version at save time, so only the paths are collected here.
 * A wide path, or one the server would not accept, upgrades the batch to a
 * full gameConfig save.
 *
 * @param {{ full?: boolean, paths?: string[] }|null} pending
 * @param {string} path
 * @returns {{ full: true }|{ full: false, paths: string[] }}
 */
export function addPersistField(pending, path) {
  if (pending && pending.full) {
    return pending;
  }
  const paths = (pending && pending.paths) || [];
  if (typeof path !== "string" || !path) {
    return { full: false, paths };
  }
  const persistPath = toPersistPath(path);
  if (!persistPath || isWidePersistPath(persistPath) || !hasPersistableRoot(persistPath)) {
    return { full: true };
  }
  if (paths.includes(persistPath)) {
    return { full: false, paths };
  }
  return { full: false, paths: [...paths, persistPath] };
}
